// src/store/slices/chatSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { sortChats } from "../../utils";

interface Chat {
  id: string;
  [key: string]: any;
}

interface ChatState {
  chats: Chat[];
  archivedChats: Chat[];
  currentChat: Chat | null;
  messagesList: any;
  msgToForward: any;
  msgsToForward: any[];
  isSelectionMode: boolean;
  initialUnreadCount: number;
  // Per-chat lastReadAt timestamps — single source of truth for divider placement.
  // Populated from IDB on startup; updated atomically when a chat is marked as read.
  lastReadTimestamps: Record<string, string>;
}

const initialState: ChatState = {
  currentChat: null,
  messagesList: [],
  chats: [],
  archivedChats: [],
  msgToForward: null,
  msgsToForward: [],
  isSelectionMode: false,
  initialUnreadCount: 0,
  lastReadTimestamps: {},
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setChats(state, action: PayloadAction<any>) {
      state.chats = action.payload.chats || state.chats;
      state.archivedChats = action.payload.archivedChats || state.archivedChats;
    },
    setMessagesList(state, action: PayloadAction<any>) {
      state.messagesList = action.payload;
    },
    addPreviousMessages(state, action: PayloadAction<any>) {
      state.messagesList = [...action.payload, ...state.messagesList];
    },
    addNewMessage(state, action: PayloadAction<any>) {
      // Check if message with same ID already exists
      const existsById = state.messagesList.some(
        (msg: any) => msg.id && msg.id === action.payload.id
      );

      if (existsById) return;

      // Also check if we're adding a real message that matches an existing temp message
      if (!action.payload.isTempMsg) {
        const tempMsgIndex = state.messagesList.findIndex(
          (msg: any) =>
            msg.isTempMsg &&
            msg.message === action.payload.message &&
            msg.senderId === action.payload.senderId
        );

        if (tempMsgIndex !== -1) {
          // Replace the temp message with the real one
          state.messagesList[tempMsgIndex] = {
            ...action.payload,
            isTempMsg: false,
          };
          return;
        }
      }

      state.messagesList = [...state.messagesList, action.payload];
    },
    updateMessageUpload(
      state,
      action: PayloadAction<{
        id: any;
        uploadProgress?: number;
        uploadStatus?: string;
        error?: string;
      }>
    ) {
      const msg = state.messagesList.find(
        (m: any) => m.id === action.payload.id
      );
      if (msg) {
        if (action.payload.uploadProgress !== undefined)
          msg.uploadProgress = action.payload.uploadProgress;
        if (action.payload.uploadStatus !== undefined)
          msg.uploadStatus = action.payload.uploadStatus;
        if (action.payload.error !== undefined)
          msg.uploadError = action.payload.error;
      }
    },
    replaceMessageById(state, action: PayloadAction<{ id: any; msg: any }>) {
      const index = state.messagesList.findIndex(
        (m: any) => m.id === action.payload.id
      );
      if (index !== -1) {
        state.messagesList[index] = { ...action.payload.msg, isTempMsg: false };
      } else {
        // Fallback: append if temp was already removed (e.g. list was reset)
        state.messagesList.push({ ...action.payload.msg, isTempMsg: false });
      }
    },
    removeLastMsg(state) {
      state.messagesList = state.messagesList.slice(0, -1);
    },
    removeMessageById(state, action: PayloadAction<any>) {
      state.messagesList = state.messagesList.filter(
        (m: any) => m.id !== action.payload
      );
    },
    deleteChat(state, action: PayloadAction<string>) {
      const chatId = action.payload;

      state.chats = state.chats.filter((c) => c.id !== chatId);
      state.archivedChats = state.archivedChats.filter((c) => c.id !== chatId);
    },
    setCurrentChat(state, action: PayloadAction<Chat | any>) {
      state.currentChat = action.payload;
    },
    setInitialUnreadCount(state, action: PayloadAction<number>) {
      state.initialUnreadCount = action.payload;
    },
    // Record the ISO timestamp of the last message a user read in a chat.
    setLastReadAt(
      state,
      action: PayloadAction<{ chatId: string; timestamp: string }>,
    ) {
      state.lastReadTimestamps[action.payload.chatId] = action.payload.timestamp;
    },
    // Bulk-load timestamps from IDB on startup (called once by ChatSidebar).
    setLastReadTimestamps(
      state,
      action: PayloadAction<Record<string, string>>,
    ) {
      state.lastReadTimestamps = {
        ...state.lastReadTimestamps,
        ...action.payload,
      };
    },
    updateCurrentChat(state, action: PayloadAction<Partial<Chat>>) {
      if (state.currentChat) {
        // Only update non-null, non-undefined values
        const payload = action.payload;
        Object.keys(payload).forEach((key) => {
          if (payload[key] !== undefined && payload[key] !== null) {
            (state.currentChat as any)[key] = payload[key];
          }
        });
      }
    },
    updateConversationList(state, action: PayloadAction<Partial<Chat>>) {
      const patchConversation = (chat: Chat) => {
        const payload = action.payload;
        Object.keys(payload).forEach((key) => {
          if (payload[key] !== undefined && payload[key] !== null) {
            (chat as any)[key] = payload[key];
          }
        });
      };

      const index = state.chats.findIndex(
        (chat) => chat.id === action.payload.id
      );
      if (index !== -1) {
        // Only update non-null, non-undefined values
        patchConversation(state.chats[index]);
        return;
      }

      const archivedIndex = state.archivedChats.findIndex(
        (chat) => chat.id === action.payload.id
      );
      if (archivedIndex !== -1) {
        patchConversation(state.archivedChats[archivedIndex]);
      } else if (action.payload.id) {
        // If it's a new conversation (like first message sent/received), add it to the list
        state.chats.unshift(action.payload as Chat);
      }
    },
    clearUnread(state, action: PayloadAction<string>) {
      const chat = state.chats.find((c) => c.id === action.payload);

      if (chat) {
        chat.unreadCount = 0;
      } else {
        const chat = state.archivedChats.find((c) => c.id === action.payload);
        if (chat) {
          chat.unreadCount = 0;
        }
      }
    },
    setMsgToForward(state, action: PayloadAction<any>) {
      state.msgToForward = action.payload;
    },
    enterSelectionMode(state) {
      state.isSelectionMode = true;
    },
    exitSelectionMode(state) {
      state.isSelectionMode = false;
      state.msgsToForward = [];
    },
    toggleMessageSelection(state, action: PayloadAction<any>) {
      const msg = action.payload;
      const index = state.msgsToForward.findIndex((m: any) => m.id === msg.id);
      if (index === -1) {
        state.msgsToForward.push(msg);
      } else {
        state.msgsToForward.splice(index, 1);
      }
    },
    archiveChat(state, action: PayloadAction<string>) {
      const chatId = action.payload;
      const chatToArchive = state.chats.find((c) => c.id === chatId);

      if (chatToArchive) {
        state.chats = state.chats.filter((c) => c.id !== chatId);
        const alreadyArchived = state.archivedChats.some(
          (c) => c.id === chatId
        );
        if (!alreadyArchived) {
          const updatedChat = { ...chatToArchive, isArchived: true };
          state.archivedChats.push(updatedChat);
        }
      }
    },
    unarchiveChat(state, action: PayloadAction<string>) {
      const chatId = action.payload;
      const chatToUnarchive = state.archivedChats.find((c) => c.id === chatId);

      if (chatToUnarchive) {
        state.archivedChats = state.archivedChats.filter(
          (c) => c.id !== chatId
        );
        const alreadyInChats = state.chats.some((c) => c.id === chatId);
        if (!alreadyInChats) {
          const updatedChat = { ...chatToUnarchive, isArchived: false };
          state.chats.unshift(updatedChat);
        }
      }
    },
    pinChat(state, action: PayloadAction<string>) {
      const chatId = action.payload;
      const chat = state.chats.find((c) => c.id === chatId);
      if (chat) {
        chat.isPin = true;
        state.chats = sortChats(state.chats);
      } else {
        const archived = state.archivedChats.find((c) => c.id === chatId);
        if (archived) {
          archived.isPin = true;
          state.archivedChats = sortChats(state.archivedChats);
        }
      }
    },
    unpinChat(state, action: PayloadAction<string>) {
      const chatId = action.payload;
      const chat = state.chats.find((c) => c.id === chatId);
      if (chat) {
        chat.isPin = false;
        state.chats = sortChats(state.chats);
      } else {
        const archived = state.archivedChats.find((c) => c.id === chatId);
        if (archived) {
          archived.isPin = false;
          state.archivedChats = sortChats(state.archivedChats);
        }
      }
    },
    muteChat(state, action: PayloadAction<string>) {
      const chatId = action.payload;
      const chat = state.chats.find((c) => c.id === chatId);
      if (chat) {
        chat.isMuted = true;
      } else {
        const archived = state.archivedChats.find((c) => c.id === chatId);
        if (archived) {
          archived.isMuted = true;
        }
      }
    },
    unmuteChat(state, action: PayloadAction<string>) {
      const chatId = action.payload;
      const chat = state.chats.find((c) => c.id === chatId);
      if (chat) {
        chat.isMuted = false;
      } else {
        const archived = state.archivedChats.find((c) => c.id === chatId);
        if (archived) {
          archived.isMuted = false;
        }
      }
    },
    moveChatToTop(
      state,
      action: PayloadAction<{ id: string }>
    ) {
      const { id } = action.payload;

      const index = state.chats.findIndex((c) => c.id === id);
      if (index === -1) return;

      state.chats[index] = {
        ...state.chats[index],
        lastUpdateToSort: new Date().toISOString(),
      };

      state.chats = sortChats(state.chats);
    },
  },
});

export const {
  setChats,
  setMessagesList,
  addPreviousMessages,
  addNewMessage,
  replaceMessageById,
  updateMessageUpload,
  removeLastMsg,
  removeMessageById,
  deleteChat,
  setCurrentChat,
  updateCurrentChat,
  clearUnread,
  setMsgToForward,
  updateConversationList,
  archiveChat,
  unarchiveChat,
  pinChat,
  unpinChat,
  muteChat,
  unmuteChat,
  moveChatToTop,
  enterSelectionMode,
  exitSelectionMode,
  toggleMessageSelection,
  setInitialUnreadCount,
  setLastReadAt,
  setLastReadTimestamps,
} = chatSlice.actions;

export default chatSlice.reducer;
